import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Eye, Edit, Trash2, GraduationCap, MapPin, Globe, Award } from 'lucide-react';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import apiService from '../../services/api';

const Universities = ({ user }) => {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState(null);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [deletingUniversity, setDeletingUniversity] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    city: '',
    logo: null,
    description: '',
    status: 'active'
  });
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    fetchUniversities();
  }, []);

  const fetchUniversities = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }
      const data = await apiService.getUniversities();
      setUniversities(data);
    } catch (error) {
      toast.error('Failed to fetch universities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('country', formData.country);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('status', formData.status);

      if (formData.logo) {
        formDataToSend.append('logo', formData.logo);
      }

      if (editingUniversity) {
        await apiService.updateUniversity(editingUniversity.id, formDataToSend);
      } else {
        await apiService.createUniversity(formDataToSend);
      }

      toast.success(`University ${editingUniversity ? 'updated' : 'created'} successfully`);
      setShowModal(false);
      setEditingUniversity(null);
      setFormData({ name: '', country: '', city: '', logo: null, description: '', status: 'active' });
      setLogoPreview(null);
      fetchUniversities();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleView = (university) => {
    setSelectedUniversity(university);
    setShowViewModal(true);
  };

  const handleEdit = (university) => {
    setEditingUniversity(university);
    setFormData({
      name: university.name,
      country: university.country,
      city: university.city,
      logo: null,
      description: university.description,
      status: university.status
    });
    setLogoPreview(university.logo ? `${process.env.REACT_APP_API_URL}${university.logo}` : null);
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, logo: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = (university) => {
    setDeletingUniversity(university);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }
      await apiService.deleteUniversity(deletingUniversity.id);
      toast.success('University deleted successfully');
      fetchUniversities();
    } catch (error) {
      toast.error(error.message || 'Failed to delete university');
    } finally {
      setShowDeleteModal(false);
      setDeletingUniversity(null);
    }
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'country', label: 'Country' },
    { key: 'city', label: 'City' },
    { key: 'status', label: 'Status' },
    {
      key: 'actions',
      label: 'Actions',
      render: (university) => (
        <div className="action-buttons">
          <motion.button
            className="btn-sm touch-target"
            onClick={() => handleView(university)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="View Details"
          >
            <Eye size={12} />
          </motion.button>
          <motion.button
            className="btn-sm warning touch-target"
            onClick={() => handleEdit(university)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Edit"
          >
            <Edit size={12} />
          </motion.button>
          <motion.button
            className="btn-sm danger touch-target"
            onClick={() => handleDelete(university)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Delete"
          >
            <Trash2 size={12} />
          </motion.button>
        </div>
      )
    }
  ];

  return (
    <div className="universities-management">
      <div className="page-header">
        <h2><GraduationCap size={24} /> Universities Management</h2>
        <motion.button
          className="btn-primary"
          onClick={() => setShowModal(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} /> Add University
        </motion.button>
      </div>

      <DataTable
        data={universities}
        columns={columns}
        loading={loading}
        emptyMessage="No universities found"
      />

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="University Details"
      >
        {selectedUniversity && (
          <div style={{ textAlign: 'left', padding: '0' }}>
            {/* Header Section */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginBottom: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem'
              }}>
                <GraduationCap size={40} />
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{selectedUniversity.name}</h3>
              <p style={{ margin: '0', opacity: '0.9' }}>University</p>
            </div>

            {/* Details Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Information</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <MapPin size={18} style={{ color: '#6b7280' }} />
                  <span style={{ color: '#374151', fontWeight: '500' }}>Location:</span>
                  <span style={{ color: '#374151' }}>{selectedUniversity.city}, {selectedUniversity.country}</span>
                </div>
              </div>
            </div>

            {/* Description */}
            {selectedUniversity.description && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Description</h4>
                <p style={{ color: '#6b7280', lineHeight: '1.6', margin: '0', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  {selectedUniversity.description}
                </p>
              </div>
            )}

            {/* Status Badge */}
            <div style={{ textAlign: 'center' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: selectedUniversity.status === 'active' ? '#dcfce7' : '#fef3c7',
                color: selectedUniversity.status === 'active' ? '#166534' : '#92400e',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: selectedUniversity.status === 'active' ? '#22c55e' : '#f59e0b',
                  borderRadius: '50%'
                }} />
                {selectedUniversity.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingUniversity(null);
          setFormData({ name: '', country: '', city: '', logo: null, description: '', status: 'active' });
          setLogoPreview(null);
        }}
        title={`${editingUniversity ? 'Edit' : 'Add'} University`}
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label>Logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
            {logoPreview && (
              <div style={{ marginTop: '10px' }}>
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  style={{
                    width: '100px',
                    height: '100px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb'
                  }}
                />
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowModal(false);
                setEditingUniversity(null);
                setFormData({ name: '', country: '', city: '', logo: null, description: '', status: 'active' });
                setLogoPreview(null);
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingUniversity ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingUniversity(null);
        }}
        title="Confirm Delete"
      >
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <p>Are you sure you want to delete this university?</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
            <button
              className="btn-sm danger"
              onClick={confirmDelete}
            >
              Delete
            </button>
            <button
              className="btn-sm"
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingUniversity(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Universities;